/**
 * MechaGuard - MapData (地图与关卡数据)
 *
 * 地图为 16 x 9 的方格，每格 64px，世界尺寸 1024 x 576。
 * 敌人沿预定义折线（waypoints）行进，折线上的格子标记为 PATH，
 * 玩家只能在 EMPTY 格上建造防御塔。
 *
 * 折线首点故意越界（x = -1），让敌人从屏幕边缘走入；
 * 出怪口标记会自动夹取到第一格可见格子。
 *
 * 关卡设计要点
 * ------------
 * 折线必须是轴对齐的，且**不能自交**（自交会让路径格判定混乱、
 * 视觉上也会出现重叠的通道）。每张地图的 path 都经过手工核对。
 */

export const TILE_SIZE = 64;
export const COLS = 16;
export const ROWS = 9;
export const WORLD_WIDTH = COLS * TILE_SIZE;   // 1024
export const WORLD_HEIGHT = ROWS * TILE_SIZE;  // 576

/** 格子类型 */
export const TileType = Object.freeze({
  EMPTY: 0,  // 可建造
  PATH: 1,   // 敌人行进路线，不可建造
  SPAWN: 2,  // 出怪口（同样不可建造）
  BASE: 3,   // 基地（同样不可建造）
});

/**
 * 关卡定义
 *
 * path      折线拐点（格子坐标）
 * hpScaleMul 波次生命成长倍率的乘数（难度）
 * speedMul   敌人基础速度乘数（难度）
 */
export const LEVELS = Object.freeze([
  {
    id: 'outpost',
    name: '前哨站',
    subtitle: '标准蛇形通道 · 适合熟悉机制',
    icon: '🛰️',
    difficulty: 1,
    path: [
      { x: -1, y: 4 },
      { x: 2, y: 4 },
      { x: 2, y: 1 },
      { x: 6, y: 1 },
      { x: 6, y: 7 },
      { x: 10, y: 7 },
      { x: 10, y: 4 },
      { x: 13, y: 4 },
      { x: 13, y: 7 },
      { x: 15, y: 7 },
    ],
    startGold: 200,
    startLives: 10,
    totalWaves: 30,
    hpScaleMul: 1.0,
    speedMul: 1.0,
  },

  {
    id: 'canyon',
    name: '峡谷通道',
    subtitle: '漫长 S 形长廊 · 敌军更硬更快',
    icon: '🏜️',
    difficulty: 2,
    path: [
      { x: -1, y: 1 },
      { x: 4, y: 1 },
      { x: 4, y: 7 },
      { x: 8, y: 7 },
      { x: 8, y: 1 },
      { x: 12, y: 1 },
      { x: 12, y: 7 },
      { x: 15, y: 7 },
    ],
    startGold: 220,
    startLives: 10,
    totalWaves: 30,
    hpScaleMul: 1.15,
    speedMul: 1.05,
  },

  {
    id: 'reactor',
    name: '核心反应堆',
    subtitle: '螺旋深入地心 · 基地居中，容错极低',
    icon: '☢️',
    difficulty: 3,
    path: [
      { x: -1, y: 1 },
      { x: 13, y: 1 },
      { x: 13, y: 7 },
      { x: 3, y: 7 },
      { x: 3, y: 3 },
      { x: 9, y: 3 },
      { x: 9, y: 5 },
      { x: 11, y: 5 },
    ],
    startGold: 250,
    startLives: 8,
    totalWaves: 30,
    hpScaleMul: 1.35,
    speedMul: 1.10,
  },
]);

/** 默认关卡 */
export const DEFAULT_LEVEL_ID = LEVELS[0].id;

/* ==========================================================
 * 自定义关卡的运行时注册表
 *
 * 为什么不直接把自定义地图 push 进 LEVELS：
 *   LEVELS 是冻结的内置数据，而且冒烟测试与无头校验都依赖
 *   「内置关卡恰好 3 张」。自定义地图是运行时产物，必须与内置数据
 *   分开存放，否则测试结果会随玩家存了几张图而漂移。
 * ========================================================== */

/** @type {Map<string, object>} */
const customLevels = new Map();

/**
 * 注册一张自定义关卡（同 id 覆盖）。
 * @param {object} level 结构与 LEVELS 中的关卡一致
 */
export function registerCustomLevel(level) {
  if (!level || !level.id) return;
  customLevels.set(level.id, level);
}

/** 注销一张自定义关卡 */
export function unregisterCustomLevel(id) {
  customLevels.delete(id);
}

/** 清空注册表（重新加载存档前调用，避免残留已删除的地图） */
export function clearCustomLevels() {
  customLevels.clear();
}

/** 当前已注册的全部自定义关卡 */
export function getCustomLevels() {
  return Array.from(customLevels.values());
}

/** 该 id 是否指向一张自定义关卡 */
export function isCustomLevel(id) {
  return customLevels.has(id);
}

/**
 * 按 id 取关卡定义。
 * 先查自定义注册表，再查内置 LEVELS；都找不到时回退到第一关。
 * @param {string} [id]
 */
export function getLevel(id = DEFAULT_LEVEL_ID) {
  const custom = customLevels.get(id);
  if (custom) return custom;
  return LEVELS.find(l => l.id === id) || LEVELS[0];
}

/** 格子中心 -> 世界坐标 */
export function tileToWorld(col, row) {
  return {
    x: (col + 0.5) * TILE_SIZE,
    y: (row + 0.5) * TILE_SIZE,
  };
}

/** 世界坐标 -> 格子坐标（向下取整） */
export function worldToTile(wx, wy) {
  return {
    col: Math.floor(wx / TILE_SIZE),
    row: Math.floor(wy / TILE_SIZE),
  };
}

/**
 * 收集一条轴对齐线段经过的所有格子。
 * @param {Set<string>} out
 */
function walkSegment(x0, y0, x1, y1, out) {
  const dx = Math.sign(x1 - x0);
  const dy = Math.sign(y1 - y0);
  let x = x0;
  let y = y0;

  out.add(`${x},${y}`);

  // 防止异常数据造成死循环
  let guard = 0;
  while ((x !== x1 || y !== y1) && guard++ < 512) {
    if (x !== x1) x += dx;
    else if (y !== y1) y += dy;
    out.add(`${x},${y}`);
  }
}

/**
 * 计算折线经过的所有格子。
 *
 * createMap() 与地图编辑器共用这一个实现 —— 编辑器里预览的通道
 * 与实际生成的地图因此**不可能不一致**。
 *
 * @param {{x:number,y:number}[]} path 折线拐点（格子坐标）
 * @returns {Set<string>} "col,row" 集合
 */
export function collectPathTiles(path) {
  const set = new Set();
  if (!Array.isArray(path)) return set;

  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    walkSegment(a.x, a.y, b.x, b.y, set);
  }
  return set;
}

/**
 * 折线的几何长度，单位是「格」。
 *
 * 用曼哈顿距离累加，与折线必须轴对齐的约束一致。
 * 这个值直接决定敌人在火力下暴露的时间，是推算难度的核心依据。
 *
 * 参考：内置三关分别是 31 / 34 / 44 格。
 *
 * @param {{x:number,y:number}[]} path
 * @returns {number}
 */
export function pathTileLength(path) {
  if (!Array.isArray(path) || path.length < 2) return 0;

  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    total += Math.abs(path[i + 1].x - path[i].x)
      + Math.abs(path[i + 1].y - path[i].y);
  }
  return total;
}

/**
 * 创建一张地图实例。
 * @param {string} [levelId] 关卡 id，缺省用第一关
 * @returns {{
 *   level: object,
 *   cols:number, rows:number, tileSize:number,
 *   width:number, height:number, tiles:number[][],
 *   waypoints:{x:number,y:number}[],
 *   spawn:{x:number,y:number}, base:{x:number,y:number},
 *   inBounds(col:number,row:number):boolean,
 *   tileAt(col:number,row:number):number,
 *   isBuildable(col:number,row:number):boolean,
 *   isPath(col:number,row:number):boolean
 * }}
 */
export function createMap(levelId = DEFAULT_LEVEL_ID) {
  const level = getLevel(levelId);
  const pathTiles = level.path;

  // 1) 初始化全空地图
  const tiles = [];
  for (let r = 0; r < ROWS; r++) {
    tiles.push(new Array(COLS).fill(TileType.EMPTY));
  }

  // 2) 标记路径经过的格子
  const pathSet = collectPathTiles(pathTiles);

  for (const key of pathSet) {
    const [cs, rs] = key.split(',');
    const c = Number(cs);
    const r = Number(rs);
    if (c >= 0 && c < COLS && r >= 0 && r < ROWS) {
      tiles[r][c] = TileType.PATH;
    }
  }

  // 3) 标注出怪口与基地
  //    折线首点故意在画面外，出怪口标记要夹到第一格可见格子。
  const spawnTile = pathTiles[0];
  const baseTile = pathTiles[pathTiles.length - 1];

  const spawnCol = Math.max(0, Math.min(COLS - 1, spawnTile.x));
  const spawnRow = Math.max(0, Math.min(ROWS - 1, spawnTile.y));
  tiles[spawnRow][spawnCol] = TileType.SPAWN;

  const baseCol = Math.max(0, Math.min(COLS - 1, baseTile.x));
  const baseRow = Math.max(0, Math.min(ROWS - 1, baseTile.y));
  tiles[baseRow][baseCol] = TileType.BASE;

  // 4) 折线转世界坐标
  const waypoints = pathTiles.map(t => tileToWorld(t.x, t.y));

  const inBounds = (col, row) => col >= 0 && col < COLS && row >= 0 && row < ROWS;

  const tileAt = (col, row) => (inBounds(col, row) ? tiles[row][col] : TileType.PATH);

  return {
    level,

    cols: COLS,
    rows: ROWS,
    tileSize: TILE_SIZE,
    width: WORLD_WIDTH,
    height: WORLD_HEIGHT,
    tiles,
    waypoints,
    spawn: tileToWorld(spawnCol, spawnRow),
    base: tileToWorld(baseCol, baseRow),

    inBounds,

    tileAt,

    /** 该格是否可建造（范围内 + 类型为 EMPTY） */
    isBuildable(col, row) {
      return inBounds(col, row) && tiles[row][col] === TileType.EMPTY;
    },

    /** 该格是否是敌人路径（含出怪口/基地） */
    isPath(col, row) {
      if (!inBounds(col, row)) return false;
      const t = tiles[row][col];
      return t === TileType.PATH || t === TileType.SPAWN || t === TileType.BASE;
    },
  };
}

/**
 * 计算折线的总长度（世界单位 / 像素）。
 * 用于平衡性核算与关卡设计校验。
 */
export function pathLength(waypoints) {
  let total = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

/**
 * 关卡自检：折线是否轴对齐、是否自交、是否落在网格内。
 * 供冒烟测试与关卡设计时使用。
 * @param {object} level
 * @returns {{ok:boolean, errors:string[]}}
 */
export function validateLevel(level) {
  const errors = [];

  if (!level || !Array.isArray(level.path) || level.path.length < 2) {
    return { ok: false, errors: ['path 至少需要 2 个拐点'] };
  }

  // 轴对齐检查
  for (let i = 0; i < level.path.length - 1; i++) {
    const a = level.path[i];
    const b = level.path[i + 1];
    if (a.x !== b.x && a.y !== b.y) {
      errors.push(`第 ${i} 段不是轴对齐: (${a.x},${a.y}) -> (${b.x},${b.y})`);
    }
    if (a.x === b.x && a.y === b.y) {
      errors.push(`第 ${i} 段长度为零: (${a.x},${a.y})`);
    }
  }

  // 自交检查：把每段展开成格子集合，同一格子只能被一段占用。
  // 相邻两段合法共享的唯一格子是它们的连接点（本段起点 = 上段终点），
  // 因此只放行这一个 key —— 这样即使一段沿原路折返覆盖上一段，
  // 也会被判定为自交而不是被误认为「连接点」。
  const owner = new Map();
  for (let i = 0; i < level.path.length - 1; i++) {
    const a = level.path[i];
    const b = level.path[i + 1];

    const cells = new Set();
    walkSegment(a.x, a.y, b.x, b.y, cells);

    // 与上一段的合法连接点
    const sharedJoint = i > 0 ? `${a.x},${a.y}` : null;

    for (const key of cells) {
      const prev = owner.get(key);

      if (prev !== undefined && prev !== i) {
        const isLegalJoint = key === sharedJoint && prev === i - 1;
        if (!isLegalJoint) {
          errors.push(`折线自交于格子 ${key}（第 ${prev} 段与第 ${i} 段）`);
        }
      }

      owner.set(key, i);
    }
  }

  // 出怪口与基地必须落在可见网格内
  const first = level.path[0];
  const last = level.path[level.path.length - 1];
  const inside = t => t.x >= 0 && t.x < COLS && t.y >= 0 && t.y < ROWS;

  if (!inside(last)) errors.push(`基地坐标越界: (${last.x},${last.y})`);
  if (first.y < 0 || first.y >= ROWS) errors.push(`出怪口行越界: y=${first.y}`);

  return { ok: errors.length === 0, errors };
}
